import { CenteredContent } from '@/shared/ui/layout/CenteredContent';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import type { LoremIpsumWidgetProps } from '../model/lorem-ipsum-widget.types';
import { useTranslation } from 'react-i18next';

export const LoremIpsumWidget = ({ data }: LoremIpsumWidgetProps) => {
  const { t } = useTranslation('contentTest');

  if (!data) {
    return (
      <SidePanel title={t('loremIpsumWidget.loremIpsum')}>
        <div className="flex items-center justify-center h-full p-4">
          <div className="text-muted-foreground">
            {t('loremIpsumWidget.noDataProvidedForLorem')}
          </div>
        </div>
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title={data.title || t('loremIpsumWidget.loremIpsum')}
      headerConfig={{
        chip: {
          label: t('loremIpsumWidget.test'),
        },
      }}
    >
      <div className="flex-1 overflow-auto bg-linear-to-br from-purple-50 to-blue-50 p-6">
        <CenteredContent>
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2 text-foreground">
              {data.title || t('loremIpsumWidget.loremIpsumWidget')}
            </h2>
            <p className="text-body-md text-muted-foreground">
              {t('loremIpsumWidget.generatedParagraphs', { count: data.items.length })}
            </p>
          </div>

          {/* Content Items */}
          <div className="space-y-4">
            {data.items.map((item) => (
              <div
                key={item.id}
                className="bg-card rounded-lg shadow-md p-6 border-l-4 border-purple-500 hover:shadow-lg transition-shadow duration-fast"
              >
                {/* Item Header */}
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center size-8 rounded-full bg-purple-100 text-purple-700 font-semibold text-body-md">
                      {item.id}
                    </span>
                    <span className="text-body-sm font-medium uppercase tracking-wide text-muted-foreground">
                      {t('loremIpsumWidget.styleLabel', { style: item.style })}
                    </span>
                  </div>
                  <span className="text-body-sm text-muted-foreground">
                    {t('loremIpsumWidget.wordCount', { wordCount: item.wordCount })}
                  </span>
                </div>

                {/* Item Text */}
                <p className="leading-relaxed text-foreground">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center text-body-sm text-muted-foreground">
            {t('loremIpsumWidget.loremIpsumTestWidgetFor')}
          </div>
        </CenteredContent>
      </div>
    </SidePanel>
  );
};
